/**
 * Intake GO_BACK Guards
 *
 * Predicates that drive the `intaking.on.GO_BACK` guard chain in
 * `medrite-checkin.machine.ts`. Exported as pure functions so unit tests
 * can exercise the same predicates the real machine uses (see
 * `intake-go-back-routing.test.ts`).
 *
 * Guard ordering in the real machine:
 *
 *   1. `isReviewMode`       → identifying      (edit/verify retraces identity)
 *   2. `hasInfoConfirmed`   → returningPatient (Looks Good → infoReview)
 *   3. fallback             → identifying      (new-patient path)
 *
 * `isReviewMode` is checked first so an in-progress edit always retraces
 * through identity, even if something left `infoConfirmed === true` on the
 * cached returning-patient output.
 */

// biome-ignore lint/suspicious/noExplicitAny: KioskMachineContext uses an index signature ([key: string]: unknown) so named-property interfaces are incompatible at call sites
type IntakeGoBackContext = any;

/**
 * True when the patient is in review or verify mode. Both paths set
 * `_reviewMode: true` on the parent context — that single flag is what this
 * guard cares about. Verify-mode-specific walking (missing-field routing,
 * verifyBack history) is handled internally by the identity arc.
 */
export function isReviewMode(context: IntakeGoBackContext): boolean {
  return !!context._reviewMode;
}

/**
 * True when the cached returning-patient output has `infoConfirmed === true`
 * — i.e. the patient hit "Looks Good" on the InfoReview card and is now
 * deeper in the flow. BACK from intake in this state should return to the
 * InfoReview card rather than the identity arc.
 */
export function hasInfoConfirmed(context: IntakeGoBackContext): boolean {
  return context._returningPatientOutput?.infoConfirmed === true;
}
